import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react'
import { useHistory } from 'react-router'
import { isIOS } from 'react-device-detect'
import '../PlaydynamexNew/PlaydynamexNew.css'

import MakeHeader from '../ReusableComponents/Header/MakeHeader'
import useIsMobile from '../../hooks/useIsMobile'

import { MdOutlineArrowBack, MdFullscreen } from 'react-icons/md'
import { BsStars } from 'react-icons/bs'
import { FaShapes } from 'react-icons/fa6'
import { GrPrevious, GrNext } from 'react-icons/gr'
import { RxReset } from 'react-icons/rx'
import getBackendImage from '../../source/imageUtil'

const BACKEND_URL = process.env.REACT_APP_API_BASE

const Playmex = () => {
    const history = useHistory()

    // Parse URL params
    const urlParams = useMemo(
        () =>
            new URLSearchParams(
                history.location?.search || window.location.search
            ),
        [history.location?.search]
    )
    const isPreviewMode = urlParams.get('preview') === 'true'
    const PROJECT_ID =
        urlParams.get('projectId') || 'default-mechanical-project'

    const HeaderComponent = MakeHeader
    const isMobile = useIsMobile()

    const audioRef = useRef(null)
    const appContainerRef = useRef(null)
    const modelViewerRef = useRef(null)
    const isModelLoadedRef = useRef(false)

    // State
    const storageKey = `mechanical_playmex_step_${PROJECT_ID}`
    const [transcript, setTranscript] = useState(null)
    const [loading, setLoading] = useState(true)

    const [currentStep, setCurrentStep] = useState(() => {
        if (isPreviewMode) return 1
        const savedStep = sessionStorage.getItem(storageKey)
        const parsed = parseInt(savedStep, 10)
        return !isNaN(parsed) && parsed > 0 ? parsed : 1
    })

    const [aiVoiceover, setAiVoiceover] = useState(
        () => sessionStorage.getItem('aiVoiceToggle') !== 'false'
    )

    const [showMaterials, setShowMaterials] = useState(
        () => sessionStorage.getItem('materialsToggle') !== 'false'
    )

    // --- Audio Logic ---
    const playAudioForStep = useCallback(
        (stepNumber) => {
            if (!audioRef.current || !transcript?.meta?.audioBaseUrl) return
            const audioUrl = `${transcript.meta.audioBaseUrl}${stepNumber}.mp3?t=${Date.now()}`
            audioRef.current.src = audioUrl
            audioRef.current
                .play()
                .catch((e) => console.log('Playback failed:', e))
        },
        [transcript]
    )

    // --- 1. Fetch Data ---
    useEffect(() => {
        const fetchTranscript = async () => {
            try {
                // Determine staging vs production endpoint based on preview mode
                const endpoint = isPreviewMode
                    ? `${BACKEND_URL}/api/staging/playmex-standalone/${PROJECT_ID}`
                    : `${BACKEND_URL}/api/playmex-standalone/${PROJECT_ID}`

                const res = await fetch(endpoint, {
                    credentials: isPreviewMode ? 'include' : 'same-origin',
                })

                if (!res.ok) throw new Error('Failed to fetch playmex data')

                const data = await res.json()
                setTranscript(data)
                setLoading(false)
            } catch (error) {
                console.error('Error loading project data:', error)
                setLoading(false)
            }
        }
        fetchTranscript()
    }, [PROJECT_ID, isPreviewMode])

    // --- 2. Trigger Initial Audio ---
    useEffect(() => {
        if (!loading && transcript && aiVoiceover) {
            playAudioForStep(currentStep)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, transcript])

    // Computed Values
    const totalSteps = useMemo(
        () => transcript?.steps?.length || 0,
        [transcript]
    )
    const modelPath = useMemo(
        () => `${transcript?.meta?.modelPath}`,
        [transcript]
    )

    const currentStepData = useMemo(() => {
        if (!transcript) return null
        return transcript.steps.find((step) => step.stepNumber === currentStep)
    }, [currentStep, transcript])

    const currentAnimationName = useMemo(() => {
        if (!currentStepData) return undefined
        return (
            currentStepData.animationName ||
            currentStep.toString().padStart(2, '0')
        )
    }, [currentStepData, currentStep])

    // --- Model Viewer Logic ---
    useEffect(() => {
        if (loading) return
        const mv = modelViewerRef.current
        if (!mv) return

        isModelLoadedRef.current = false

        const handleLoad = () => {
            // Safely parse as Number, fallback strictly to 1.0 for older projects
            const targetSpeed =
                currentStepData?.speed !== undefined
                    ? Number(currentStepData.speed)
                    : 1.0
            mv.timeScale = targetSpeed > 0 ? targetSpeed : 1.0
            isModelLoadedRef.current = true
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    const m = modelViewerRef.current
                    if (!m) return
                    m.currentTime = 0
                    m.play({ repetitions: 1 })
                })
            })
        }

        mv.addEventListener('load', handleLoad)
        return () => mv.removeEventListener('load', handleLoad)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, modelPath]) // currentStepData intentionally omitted

    useEffect(() => {
        const mv = modelViewerRef.current
        if (!mv || !currentAnimationName || !isModelLoadedRef.current) return

        let raf1, raf2
        let isCancelled = false

        raf1 = requestAnimationFrame(() => {
            raf2 = requestAnimationFrame(() => {
                if (isCancelled) return
                const m = modelViewerRef.current
                if (!m) return
                m.currentTime = 0

                // Read speed from current step data, default strictly to 1.0 if missing
                const targetSpeed =
                    currentStepData?.speed !== undefined
                        ? Number(currentStepData.speed)
                        : 1.0
                m.timeScale = targetSpeed > 0 ? targetSpeed : 1.0

                m.play({ repetitions: 1 })
            })
        })

        return () => {
            isCancelled = true
            cancelAnimationFrame(raf1)
            cancelAnimationFrame(raf2)
        }
    }, [currentAnimationName, currentStepData])

    // --- Subtitle & UI State ---
    const [showSubtitleDisplay, setShowSubtitleDisplay] = useState(false)
    const [subtitleVisible, setSubtitleVisible] = useState(false)
    const [typewriterText, setTypewriterText] = useState('')
    const subtitleRef = useRef(null)
    const [aiSpeaking, setAiSpeaking] = useState(true)

    // Refined Typewriter Effect
    useEffect(() => {
        if (aiVoiceover && currentStepData?.instruction) {
            setTypewriterText('')
            setAiSpeaking(true)

            let i = 0
            const text = currentStepData.instruction
            const interval = setInterval(() => {
                i++
                setTypewriterText(text.slice(0, i))

                if (i >= text.length) {
                    clearInterval(interval)
                    setAiSpeaking(true)
                }
            }, 40)
            return () => clearInterval(interval)
        } else {
            setTypewriterText(currentStepData?.instruction || '')
            setAiSpeaking(false)
        }
    }, [aiVoiceover, currentStepData])

    // Hide subtitle display on outside click
    useEffect(() => {
        const handleClick = (e) => {
            if (
                subtitleRef.current &&
                !subtitleRef.current.contains(e.target)
            ) {
                setShowSubtitleDisplay(false)
                setAiSpeaking(true)
            }
        }
        if (showSubtitleDisplay)
            document.addEventListener('mousedown', handleClick)
        return () => document.removeEventListener('mousedown', handleClick)
    }, [showSubtitleDisplay])

    // Slide in subtitle on mount
    useEffect(() => {
        const t = setTimeout(() => {
            setSubtitleVisible(true)
            setShowSubtitleDisplay(true)
        }, 50)
        return () => clearTimeout(t)
    }, [])

    useEffect(() => {
        sessionStorage.setItem(storageKey, currentStep.toString())
    }, [currentStep, storageKey])

    // --- Navigation Handlers ---
    const resetTutorial = useCallback(() => {
        setCurrentStep(1)
        setShowSubtitleDisplay(true)
        setAiSpeaking(true)
        if (aiVoiceover) playAudioForStep(1)
    }, [aiVoiceover, playAudioForStep])

    const handleNext = useCallback(() => {
        if (currentStep < totalSteps) {
            const nextStep = currentStep + 1
            setCurrentStep(nextStep)
            if (aiVoiceover) playAudioForStep(nextStep)
            setAiSpeaking(false)
            setShowSubtitleDisplay(true)
        }
    }, [currentStep, totalSteps, aiVoiceover, playAudioForStep])

    const handlePrev = useCallback(() => {
        if (currentStep > 1) {
            const prevStep = currentStep - 1
            setCurrentStep(prevStep)
            if (aiVoiceover) playAudioForStep(prevStep)
        }
        setAiSpeaking(false)
        setShowSubtitleDisplay(true)
    }, [currentStep, aiVoiceover, playAudioForStep])

    const goBack = useCallback(() => {
        history.goBack()
    }, [history])

    const handleFullscreen = useCallback(() => {
        const el = appContainerRef.current
        const isFullscreen =
            document.fullscreenElement || document.webkitFullscreenElement
        if (!isFullscreen) {
            if (el?.requestFullscreen) el.requestFullscreen()
            else if (el?.webkitRequestFullscreen) el.webkitRequestFullscreen()
        } else {
            if (document.exitFullscreen) document.exitFullscreen()
            else if (document.webkitExitFullscreen)
                document.webkitExitFullscreen()
        }
    }, [])

    const handleMaterialsToggle = useCallback(() => {
        setShowMaterials((prev) => {
            const nextState = !prev
            sessionStorage.setItem('materialsToggle', String(nextState))
            return nextState
        })
    }, [])

    const handleAiVoiceoverToggle = useCallback(() => {
        const nextState = !aiVoiceover
        setAiVoiceover(nextState)
        sessionStorage.setItem('aiVoiceToggle', String(nextState))

        if (nextState) {
            playAudioForStep(currentStep)
        } else {
            if (audioRef.current) {
                audioRef.current.pause()
                audioRef.current.currentTime = 0
            }
        }
    }, [aiVoiceover, currentStep, playAudioForStep])

    // --- Renders ---
    if (loading) {
        return (
            <div
                className="pdx-app-container"
                style={{
                    color: 'white',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                Loading Mechanical Project...
            </div>
        )
    }

    if (!transcript) {
        return (
            <div className="pdx-app-container">Error loading project data.</div>
        )
    }

    return (
        <div className="pdx-app-container" ref={appContainerRef}>
            {isPreviewMode && (
                <button
                    onClick={goBack}
                    style={{
                        position: 'fixed',
                        top: '12vh',
                        right: '30px',
                        zIndex: 9999,
                        background: '#ef4444',
                        color: 'white',
                        border: 'none',
                        padding: '12px 24px',
                        borderRadius: '50px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        boxShadow: '0 4px 15px rgba(239, 68, 68, 0.4)',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                    }}
                >
                    <MdOutlineArrowBack size={18} /> Exit Preview
                </button>
            )}

            <div className="pdx-header">
                <HeaderComponent
                    showTimeline={isPreviewMode}
                    title={transcript.title}
                    showJumpTo={false}
                    titlePosition="left"
                    help_btnPosition="right"
                    isGoBack={!isIOS}
                    goBack={isIOS ? undefined : goBack}
                    showHelp={true}
                    showBluetoothBtn={false}
                    currentStep={currentStep}
                    totalSteps={totalSteps}
                    stepTitles={
                        transcript?.steps?.map((step) => step?.title || '') ||
                        []
                    }
                    onStepClick={(stepNum) => {
                        setCurrentStep(stepNum)
                        if (aiVoiceover) playAudioForStep(stepNum)
                        setAiSpeaking(false)
                        setShowSubtitleDisplay(true)
                    }}
                />
            </div>

            <main className="pdx-viewer-container">
                {currentStepData?.instruction && (
                    <div
                        ref={subtitleRef}
                        style={{
                            position: 'absolute',
                            top: '10px',
                            left: '50%',
                            transform: !subtitleVisible
                                ? 'translateX(-50%) translateY(-120vh)'
                                : 'translateX(-50%)',
                            maxWidth: !showSubtitleDisplay ? '280px' : '600px',
                            maxHeight: !showSubtitleDisplay ? '44px' : '150px',
                            borderRadius: !showSubtitleDisplay
                                ? '20px'
                                : '20px',
                            padding: !showSubtitleDisplay
                                ? '0 18px'
                                : '12px 24px',
                            background: !showSubtitleDisplay
                                ? 'rgba(60, 60, 120, 0.13)'
                                : 'rgba(255, 255, 255, 0.3)',
                            backdropFilter: 'blur(10px)',
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                            border: '1px solid rgba(0, 0, 0, 0.08)',
                            display: 'grid',
                            gridTemplateRows: !showSubtitleDisplay
                                ? '44px'
                                : 'auto',
                            alignItems: 'center',
                            justifyItems: 'center',
                            overflow: 'hidden',
                            zIndex: 101,
                            cursor: !showSubtitleDisplay
                                ? 'pointer'
                                : 'default',
                            opacity: !subtitleVisible ? 0 : 1,
                            pointerEvents: !subtitleVisible ? 'none' : 'auto',
                            transition: [
                                'transform 1s cubic-bezier(0.4, 0, 0.2, 1)',
                                'max-width 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                                'max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                                'border-radius 0.45s ease',
                                'padding 0.4s ease',
                                'background 0.45s ease',
                                'opacity 0.6s ease',
                            ].join(', '),
                        }}
                        onClick={
                            !showSubtitleDisplay
                                ? () => setShowSubtitleDisplay(true)
                                : undefined
                        }
                    >
                        <div
                            style={{
                                gridArea: '1/1',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px',
                                fontWeight: 600,
                                fontSize: '1rem',
                                color: '#2f2e65',
                                fontFamily: 'Halcyon_SemiBold, sans-serif',
                                whiteSpace: 'nowrap',
                                opacity: !showSubtitleDisplay ? 1 : 0,
                                transition: 'opacity 0.2s ease',
                                transitionDelay: !showSubtitleDisplay
                                    ? '0.25s'
                                    : '0s',
                                pointerEvents: 'none',
                            }}
                        >
                            <BsStars size={20} style={{ color: '#2f2e65' }} />
                            {aiVoiceover
                                ? 'AI is speaking...'
                                : 'Click to expand'}
                        </div>
                        <div
                            style={{
                                gridArea: '1/1',
                                fontSize: '1.1rem',
                                color: '#333',
                                wordBreak: 'break-word',
                                whiteSpace: 'pre-wrap',
                                fontFamily: 'Halcyon Regular, sans-serif',
                                lineHeight: '1.5',
                                textAlign: 'center',
                                opacity: !showSubtitleDisplay ? 0 : 1,
                                transition: 'opacity 0.2s ease',
                                transitionDelay: !showSubtitleDisplay
                                    ? '0s'
                                    : '0.25s',
                                pointerEvents: 'none',
                                width: '100%',
                            }}
                        >
                            {aiVoiceover
                                ? typewriterText
                                : currentStepData.instruction}
                        </div>
                    </div>
                )}

                <model-viewer
                    key={modelPath}
                    ref={modelViewerRef}
                    src={modelPath}
                    animation-name={currentAnimationName}
                    animation-crossfade-duration="0"
                    camera-controls
                    shadow-intensity="1"
                />

                {currentStepData?.materials && (
                    <div
                        className={`materials-panel ${showMaterials ? 'visible' : ''}`}
                    >
                        {Object.entries(currentStepData.materials).map(
                            ([materialName, quantity]) => (
                                <div
                                    key={materialName}
                                    className="material-item"
                                >
                                    <div className="material-image-wrapper">
                                        <img
                                            src={getBackendImage(`${materialName}.png`)}
                                            alt={materialName}
                                            className="material-image"
                                            onError={(e) => {
                                                e.target.style.display = 'none'
                                            }}
                                        />
                                    </div>
                                    <div className="material-label">
                                        {materialName.replace(/_/g, ' ')} x{' '}
                                        {quantity}
                                    </div>
                                </div>
                            )
                        )}
                    </div>
                )}

                <div className="toolbar-sidebar">
                    <button
                        className={`toolbar-button ${showMaterials ? 'active' : ''}`}
                        title="Materials"
                        onClick={handleMaterialsToggle}
                    >
                        <FaShapes />
                    </button>
                    <button
                        className={`toolbar-button ${aiVoiceover ? 'active' : ''}`}
                        title="AI Voiceover"
                        onClick={() => {
                            handleAiVoiceoverToggle()
                            setSubtitleVisible(true)
                            setShowSubtitleDisplay(true)
                        }}
                    >
                        <BsStars />
                    </button>
                    {!isMobile && (
                        <button
                            className="toolbar-button"
                            title="Fullscreen"
                            onClick={handleFullscreen}
                        >
                            <MdFullscreen />
                        </button>
                    )}
                    <button
                        className="toolbar-button"
                        title="Reset"
                        onClick={resetTutorial}
                    >
                        <RxReset />
                    </button>
                </div>

                <audio ref={audioRef} />
            </main>

            <footer className="pdx-footer">
                <div className="step-counter">
                    Step {currentStep} of {totalSteps}
                </div>
                <div className="step-title">{currentStepData?.title}</div>
                <div className="nav-buttons">
                    <button
                        className="nav-btn"
                        onClick={handlePrev}
                        disabled={currentStep === 1}
                    >
                        <GrPrevious />
                        <span className="footer-btn-text">Previous</span>
                    </button>
                    <button
                        className="nav-btn"
                        onClick={handleNext}
                        disabled={currentStep >= totalSteps}
                    >
                        <span className="footer-btn-text">
                            {/* {currentStep === totalSteps ? 'Finish' : 'Next'} */}
                            Next
                        </span>
                        <GrNext />
                    </button>
                </div>
            </footer>
        </div>
    )
}

export default Playmex
